import * as React from 'react';

/**
 * Inline callout box. The `error` variant is byte-identical to the dominant
 * hand-rolled error box it replaces (`bg-red-50 border border-red-200
 * text-red-700 px-4 py-3 rounded-lg`); the other variants follow the same
 * structure in their palette.
 *
 * `role` defaults to "alert" for error/warning (announced by screen readers)
 * and "status" for info/success.
 */

export type AlertVariant = 'error' | 'warning' | 'info' | 'success';

const VARIANT_CLASSES: Record<AlertVariant, string> = {
  error: 'bg-red-50 border border-red-200 text-red-700',
  warning: 'bg-amber-50 border border-amber-200 text-amber-800',
  info: 'bg-blue-50 border border-blue-200 text-blue-800',
  success: 'bg-green-50 border border-green-200 text-green-800',
};

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
}

export function Alert({ variant = 'info', className, children, role, ...props }: AlertProps) {
  const defaultRole = variant === 'error' || variant === 'warning' ? 'alert' : 'status';
  const classes = [VARIANT_CLASSES[variant], 'px-4 py-3 rounded-lg', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div role={role ?? defaultRole} className={classes} {...props}>
      {children}
    </div>
  );
}

export default Alert;
